import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { MapsProvider } from './components/MapsProvider';
import { AppLayout } from './layouts/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { ForcedPasswordReset } from './pages/ForcedPasswordReset';
import { Onboarding } from './pages/Onboarding';
import { AdminDashboard, AdminOnboardingDashboardPage, AdminCustomers, AdminPurchaseOrders, AdminInventory, AdminRoutes, AdminRouteDensity, AdminStoreTiering, AdminSales, AdminCategories, AdminOrders, AdminProductCatalog, AdminPromotions, AdminStockManagement, AdminAccounting, AdminTaxCompliance, AdminInvoices, AdminPayments, AdminLiveTracking, AdminProfile, AdminUsers } from './pages/admin';
import { CustomerDashboard, CustomerNotifications, CustomerProfile, CustomerCatalog, CustomerCart, CustomerOrderDetails, CustomerActivityHub, CustomerReportIssue, CustomerLiveTracking } from './pages/customer';
import { WarehouseDashboard, WarehouseProfile, WarehouseScanner, WarehousePickPack, WarehouseReceiving, WarehouseInventory, WarehouseTransfers, WarehouseReturns, WarehouseReports, CounterSalesPOS, CounterSalesHistory } from './pages/warehouse';
import { DriverDashboard, DriverProfile, DriverRoutes, DriverActiveDeliveries, DriverCollections, DriverFailedDeliveries, DriverHistory, DriverReturns, DriverPerformance } from './pages/driver';
import { CallCenterDashboard, CallCenterProfile, CallCenterAllTickets, CallCenterCustomerIssues, CallCenterDeliveryIssues, CallCenterPaymentIssues, CallCenterInventoryIssues, CallCenterComplianceIssues, CallCenterReturnsDisputes, CallCenterKnowledgeBase } from './pages/callcenter';
import { SalesRepDashboard, SalesRepProfile, SalesRepCustomers, SalesRepCustomerDetails, SalesRepOrders, SalesRepOrderDetails, SalesRepNewOrder, SalesRepActivities, SalesRepLogActivity, SalesRepGoalsTargets, SalesRepAiInsights, SalesRepPriceMatchRequests } from './pages/salesrep';
import { ComingSoonPage } from './pages/ComingSoonPage';
import './App.css';

/**
 * Dynamic dashboard router — shows the correct dashboard based on user role
 */
const DashboardRouter: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();

  switch (role) {
    case 'admin':
      return <AdminDashboard />;
    case 'customer':
      return <CustomerDashboard />;
    case 'warehouse manager':
      return <WarehouseDashboard />;
    case 'driver':
      return <DriverDashboard />;
    case 'call center':
      return <CallCenterDashboard />;
    case 'sales rep':
      return <SalesRepDashboard />;
    default:
      return <ComingSoonPage />;
  }
};

/**
 * VP-CUST-003/VP-FIELD-001: `/live-tracking` is shared by Admin, Driver and
 * Customer, but each needs a different view — the admin fleet map is the
 * wrong (and over-privileged) experience for a Customer, who should only
 * ever see their own active delivery. This mirrors DashboardRouter's
 * role-switch pattern.
 */
const LiveTrackingRouter: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();

  if (role === 'customer') {
    return <CustomerLiveTracking />;
  }
  return <AdminLiveTracking />;
};

/**
 * Dynamic profile router — shows the correct profile based on user role
 */
const ProfileRouter: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();

  switch (role) {
    case 'admin':
      return <AdminProfile />;
    case 'customer':
      return <CustomerProfile />;
    case 'warehouse manager':
      return <WarehouseProfile />;
    case 'driver':
      return <DriverProfile />;
    case 'call center':
      return <CallCenterProfile />;
    case 'sales rep':
      return <SalesRepProfile />;
    default:
      return <ComingSoonPage />;
  }
};

/**
 * Dynamic customers router — different list view per role
 */
const CustomersRouter: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();

  switch (role) {
    case 'sales rep':
      return <SalesRepCustomers />;
    case 'admin':
      return <AdminCustomers />;
    default:
      return <AdminCustomers />;
  }
};

/**
 * Dynamic orders router — different list view per role
 */
const OrdersRouter: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();

  switch (role) {
    case 'sales rep':
      return <SalesRepOrders />;
    case 'admin':
    case 'customer':
      return <AdminOrders />;
    default:
      return <AdminOrders />;
  }
};

/**
 * Dynamic inventory router — shows the correct inventory based on user role
 */
const InventoryRouter: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();

  switch (role) {
    case 'admin':
      return <AdminInventory />;
    case 'warehouse manager':
      return <WarehouseInventory />;
    default:
      return <ComingSoonPage />;
  }
};

/**
 * Dynamic returns router — shows the correct returns page based on user role
 */
const ReturnsRouter: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();

  switch (role) {
    case 'warehouse manager':
      return <WarehouseReturns />;
    case 'driver':
      return <DriverReturns />;
    default:
      return <ComingSoonPage />;
  }
};

/**
 * Dynamic routes router — shows the correct routes page based on user role
 */
const RoutesRouter: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();

  switch (role) {
    case 'driver':
      return <DriverRoutes />;
    case 'admin':
    case 'warehouse manager':
      return <AdminRoutes />;
    default:
      return <ComingSoonPage />;
  }
};

const queryClient = new QueryClient();

/**
 * Role constants matching AuthContext static users
 * ProtectedRoute does case-insensitive comparison
 */
const ROLES = {
  ADMIN: 'Admin',
  SALES_REP: 'Sales Rep',
  DRIVER: 'Driver',
  WAREHOUSE: 'Warehouse Manager',
  CALL_CENTER: 'Call Center',
  CUSTOMER: 'Customer',
} as const;

const ALL_ROLES = Object.values(ROLES);

/**
 * Main App component with routing
 * Handles authentication-based routing and page navigation
 */
function AppRoutes() {
  const { isSignedIn, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <div style={{ color: 'white', fontSize: 18, fontWeight: 600 }}>
          Loading VIN Pulse OS...
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes (no layout) */}
      <Route
        path="/login"
        element={isSignedIn ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route
        path="/onboarding"
        element={<Onboarding />}
      />

      {/* Forced password reset — authenticated, but exempt from the password gate
          itself (otherwise it would redirect to itself forever). No AppLayout so
          the user can't navigate away until they've set a password. */}
      <Route
        path="/change-password"
        element={
          <ProtectedRoute requiredRoles={ALL_ROLES} allowPasswordChange>
            <ForcedPasswordReset />
          </ProtectedRoute>
        }
      />

      {/* Dashboard — All roles, content varies by role */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRoles={ALL_ROLES}>
            <AppLayout>
              <DashboardRouter />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Admin-only routes ===== */}
      <Route
        path="/admin/onboarding"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminOnboardingDashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/route-density"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminRouteDensity />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/store-tiering"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminStoreTiering />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminSales />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/categories"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminCategories />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-orders"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminPurchaseOrders />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock-management"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminStockManagement />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/promotions"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminPromotions />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tax-compliance"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminTaxCompliance />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounting"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminAccounting />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminPayments />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Admin + Sales Rep ===== */}
      <Route
        path="/ai-insights"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.SALES_REP]}>
            <AppLayout>
              <SalesRepAiInsights />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.SALES_REP]}>
            <AppLayout>
              <CustomersRouter />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN]}>
            <AppLayout>
              <AdminUsers />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/price-match-requests"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.SALES_REP]}>
            <AppLayout>
              <SalesRepPriceMatchRequests />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-details"
        element={
          <ProtectedRoute requiredRoles={[ROLES.SALES_REP]}>
            <AppLayout>
              <SalesRepCustomerDetails />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Admin + Warehouse Manager ===== */}
      <Route
        path="/inventory"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.WAREHOUSE]}>
            <AppLayout>
              <InventoryRouter />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Admin + Driver + Warehouse Manager ===== */}
      <Route
        path="/routes"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.DRIVER, ROLES.WAREHOUSE]}>
            <AppLayout>
              <RoutesRouter />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Admin + Sales Rep + Customer ===== */}
      <Route
        path="/orders"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.SALES_REP, ROLES.CUSTOMER]}>
            <AppLayout>
              <OrdersRouter />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/order-details"
        element={
          <ProtectedRoute requiredRoles={[ROLES.SALES_REP]}>
            <AppLayout>
              <SalesRepOrderDetails />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/new-order"
        element={
          <ProtectedRoute requiredRoles={[ROLES.SALES_REP]}>
            <AppLayout>
              <SalesRepNewOrder />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Admin + Customer ===== */}
      <Route
        path="/product-catalog"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.CUSTOMER]}>
            <AppLayout>
              <AdminProductCatalog />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.CUSTOMER]}>
            <AppLayout>
              <AdminInvoices />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Customer only ===== */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CUSTOMER]}>
            <AppLayout>
              <CustomerNotifications />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.CUSTOMER, ROLES.WAREHOUSE, ROLES.DRIVER, ROLES.CALL_CENTER, ROLES.SALES_REP]}>
            <AppLayout>
              <ProfileRouter />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CUSTOMER]}>
            <AppLayout>
              <CustomerCatalog />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-cart"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CUSTOMER]}>
            <AppLayout>
              <CustomerCart />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* VP-CUST-003: Orders / Deliveries / Invoices / Returns are now tabs of a
          single consolidated hub (CustomerActivityHub). Each original route is
          kept so existing links/bookmarks/nav-menu entries still work — they
          render the same hub with the matching tab pre-selected. /customer-returns
          is new (no prior route existed for it). Tab switching client-side
          navigates between these four routes (see CustomerActivityHub). */}
      <Route
        path="/customer-orders"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CUSTOMER]}>
            <AppLayout>
              <CustomerActivityHub initialTab="orders" />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-order-details"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CUSTOMER]}>
            <AppLayout>
              <CustomerOrderDetails />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-deliveries"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CUSTOMER]}>
            <AppLayout>
              <CustomerActivityHub initialTab="deliveries" />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-invoices"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CUSTOMER]}>
            <AppLayout>
              <CustomerActivityHub initialTab="invoices" />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-returns"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CUSTOMER]}>
            <AppLayout>
              <CustomerActivityHub initialTab="returns" />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-report-issue"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CUSTOMER]}>
            <AppLayout>
              <CustomerReportIssue />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Admin + Driver + Customer ===== */}
      {/* VP-CUST-003/VP-FIELD-001: Customers get a scoped delivery-status view
          (their own active delivery only), not the admin fleet map. */}
      <Route
        path="/live-tracking"
        element={
          <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.DRIVER, ROLES.CUSTOMER]}>
            <AppLayout>
              <LiveTrackingRouter />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Sales Rep only ===== */}
      <Route
        path="/activities"
        element={
          <ProtectedRoute requiredRoles={[ROLES.SALES_REP]}>
            <AppLayout>
              <SalesRepActivities />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/log-activity"
        element={
          <ProtectedRoute requiredRoles={[ROLES.SALES_REP]}>
            <AppLayout>
              <SalesRepLogActivity />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/goals-targets"
        element={
          <ProtectedRoute requiredRoles={[ROLES.SALES_REP]}>
            <AppLayout>
              <SalesRepGoalsTargets />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Driver only ===== */}
      <Route
        path="/active-deliveries"
        element={
          <ProtectedRoute requiredRoles={[ROLES.DRIVER]}>
            <AppLayout>
              <DriverActiveDeliveries />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/collections"
        element={
          <ProtectedRoute requiredRoles={[ROLES.DRIVER]}>
            <AppLayout>
              <DriverCollections />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/failed-deliveries"
        element={
          <ProtectedRoute requiredRoles={[ROLES.DRIVER]}>
            <AppLayout>
              <DriverFailedDeliveries />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute requiredRoles={[ROLES.DRIVER]}>
            <AppLayout>
              <DriverHistory />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance"
        element={
          <ProtectedRoute requiredRoles={[ROLES.DRIVER]}>
            <AppLayout>
              <DriverPerformance />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Driver + Warehouse Manager ===== */}
      <Route
        path="/returns"
        element={
          <ProtectedRoute requiredRoles={[ROLES.DRIVER, ROLES.WAREHOUSE]}>
            <AppLayout>
              <ReturnsRouter />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Warehouse Manager only ===== */}
      <Route
        path="/pick-pack"
        element={
          <ProtectedRoute requiredRoles={[ROLES.WAREHOUSE]}>
            <AppLayout>
              <WarehousePickPack />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/receiving"
        element={
          <ProtectedRoute requiredRoles={[ROLES.WAREHOUSE]}>
            <AppLayout>
              <WarehouseReceiving />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfers"
        element={
          <ProtectedRoute requiredRoles={[ROLES.WAREHOUSE]}>
            <AppLayout>
              <WarehouseTransfers />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute requiredRoles={[ROLES.WAREHOUSE]}>
            <AppLayout>
              <WarehouseReports />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scan-products"
        element={
          <ProtectedRoute requiredRoles={[ROLES.WAREHOUSE]}>
            <AppLayout>
              <WarehouseScanner />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/warehouse-scanner"
        element={
          <ProtectedRoute requiredRoles={[ROLES.WAREHOUSE]}>
            <AppLayout>
              <WarehouseScanner />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/counter-sales"
        element={
          <ProtectedRoute requiredRoles={[ROLES.WAREHOUSE, ROLES.ADMIN]}>
            <AppLayout>
              <CounterSalesPOS />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/counter-sales-history"
        element={
          <ProtectedRoute requiredRoles={[ROLES.WAREHOUSE, ROLES.ADMIN]}>
            <AppLayout>
              <CounterSalesHistory />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* ===== Call Center only ===== */}
      <Route
        path="/support"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CALL_CENTER]}>
            <AppLayout>
              <CallCenterAllTickets />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-issues"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CALL_CENTER]}>
            <AppLayout>
              <CallCenterCustomerIssues />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/delivery-issues"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CALL_CENTER]}>
            <AppLayout>
              <CallCenterDeliveryIssues />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-issues"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CALL_CENTER]}>
            <AppLayout>
              <CallCenterPaymentIssues />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory-issues"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CALL_CENTER]}>
            <AppLayout>
              <CallCenterInventoryIssues />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance-issues"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CALL_CENTER]}>
            <AppLayout>
              <CallCenterComplianceIssues />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/returns-disputes"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CALL_CENTER]}>
            <AppLayout>
              <CallCenterReturnsDisputes />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base"
        element={
          <ProtectedRoute requiredRoles={[ROLES.CALL_CENTER]}>
            <AppLayout>
              <CallCenterKnowledgeBase />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Root and Catch-all */}
      <Route
        path="/"
        element={isSignedIn ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MapsProvider>
        <Router>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
      </MapsProvider>
    </QueryClientProvider>
  );
}

export default App;
